import { useContext } from 'react';
import { alpha, useTheme } from '@classify/react-core-components/styles';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import { makeStyles } from '@mui/styles';
import { ActionsMenu } from '@openclassrooms/ui-web/ui';
import Typography from '@classify/react-core-components/Typography';
import MoreIconButton from '@openclassrooms/ui-web/ui/components/ActionsMenu/MoreIconButton';
import { translate } from '@openclassrooms/translations';
import { useChapterCancelCompletion } from '@openclassrooms/data-connect-web/chapters';
import CourseChapterContext from '../../contexts/courseChapterContext';
import CourseContext from '../../contexts/courseContext';

const useStyles = makeStyles(({ spacing, breakpoints, palette }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing(2),
    [breakpoints.up('sm')]: {
      padding: spacing(2, 11),
    },
  },
  icon: {
    marginRight: spacing(1),
  },
  menu: {
    padding: '0',
    marginLeft: 'auto',
  },
  menuButton: {
    color: palette.text.primary,
    padding: '0',
  },
}));

interface ChapterCompletionBannerProps {
  onClick: () => void;
}

const ChapterCompletionBanner = ({ onClick }: ChapterCompletionBannerProps) => {
  const { currentUser, refetchTocData, refetchCourseData } = useContext(CourseContext);
  const { currentChapter } = useContext(CourseChapterContext);

  const { id, isCompleted } = currentChapter;

  const [cancelChapterCompletion] = useChapterCancelCompletion({
    onSuccess() {
      refetchTocData();
      refetchCourseData();
    },
  });

  const classes = useStyles();
  const { palette } = useTheme();

  const backgroundColor = isCompleted
    ? alpha(palette.status.success, 0.1)
    : alpha(palette.status.info, 0.1);

  const label = isCompleted
    ? translate('course.chapter_status.complete')
    : translate('course.chapter_status.incomplete');

  const handleChapterCancelCompletion = () => {
    if (!currentUser) return;

    if (isCompleted) {
      cancelChapterCompletion(id);
      onClick();
    }
  };

  return (
    <div className={classes.root} style={{ backgroundColor: backgroundColor.toString() }}>
      {isCompleted ? (
        <CheckCircle className={classes.icon} htmlColor={palette.status.success} />
      ) : (
        <Error className={classes.icon} htmlColor={palette.background.information} />
      )}

      <Typography variant="subheading" component="span">
        {label}
      </Typography>

      {isCompleted && (
        <div className={classes.menu} data-testid="completionBannerAction">
          <ActionsMenu
            align="right"
            translations={{
              ariaLabelMore: translate('global.word.more'),
            }}
            button={
              <MoreIconButton
                ariaLabel={translate('global.word.more')}
                className={classes.menuButton}
              />
            }
          >
            <ActionsMenu.Item
              label={translate('course.chapter_status.mark_incomplete.cta')}
              onClick={handleChapterCancelCompletion}
            />
          </ActionsMenu>
        </div>
      )}
    </div>
  );
};

export default ChapterCompletionBanner;
